import { useEffect, useRef, useState } from 'react';

/*
 * central-chat (central.chat): para activarlo, poné el channel-key del panel de
 * central.chat ("businessId|chatAccountId") en REACT_APP_CENTRAL_CHAT_KEY (.env).
 * Sin channel-key, o si el chat no carga, se ofrece WhatsApp.
 */
const CENTRAL_CHAT_SRC = process.env.REACT_APP_CENTRAL_CHAT_SRC || 'https://web.central.chat/widget/core.js';
export const CENTRAL_CHAT_KEY = process.env.REACT_APP_CENTRAL_CHAT_KEY || '';
const CENTRAL_CHAT_TIMEOUT_MS = 12000;
const PREFILL_DELAY_MS = 1500;
const LITO_WHATSAPP = '5492645702020';

/* Query param that turns the app into the full-page Lito chat: /?lito=IM123 */
export const LITO_PAGE_PARAM = 'lito';
const LITO_CODE_RE = /^(IM|CO)[A-Za-z0-9]*$/;

/* Taxpayer code (IM<id> or CO<id>) from the URL, or null if it isn't one */
export function parseLitoCode(value) {
    return typeof value === 'string' && LITO_CODE_RE.test(value) ? value : null;
}

export function litoMessage(code) {
    return `¡Hola Lito!, te consulto por este ID de contribuyente: ${code}`;
}

export function litoWhatsAppUrl(message) {
    return `https://wa.me/${LITO_WHATSAPP}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
}

/* Opens the full-page chat in a new tab; false if central-chat isn't configured or the tab was blocked */
export function openLitoChatInNewTab(code) {
    if (!CENTRAL_CHAT_KEY) return false;
    const url = `${window.location.origin}${process.env.PUBLIC_URL || ''}/?${LITO_PAGE_PARAM}=${encodeURIComponent(code)}`;
    const tab = window.open(url, '_blank');
    if (!tab) return false;
    tab.opener = null;
    return true;
}

let scriptPromise = null;

/* Loads the central-chat script once and resolves when <central-chat> is defined */
function loadCentralChatScript() {
    if (customElements.get('central-chat')) return Promise.resolve();
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            customElements.whenDefined('central-chat').then(resolve);
            const script = document.createElement('script');
            script.src = CENTRAL_CHAT_SRC;
            script.async = true;
            script.onerror = () => reject(new Error('central-chat load error'));
            document.head.appendChild(script);
        });
        scriptPromise.catch(() => { scriptPromise = null; });
    }
    return scriptPromise;
}

function withTimeout(promise, ms) {
    return Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('central-chat timeout')), ms)),
    ]);
}

/*
 * Mounts central-chat inside hostRef while `active` (unmounting destroys it) and proposes `message`
 * in its composer. Returns 'off' | 'loading' | 'ready' | 'error'; on 'off'/'error' use WhatsApp.
 */
export function useCentralChat({ active, message, hostRef }) {
    const [status, setStatus] = useState(CENTRAL_CHAT_KEY ? 'loading' : 'off');
    const [conversationOpen, setConversationOpen] = useState(false);
    const chatRef = useRef(null);
    const openedAtRef = useRef(0);

    useEffect(() => {
        if (!active || !CENTRAL_CHAT_KEY) return;
        let cancelled = false;
        let widget = null;
        const start = loadCentralChatScript().then(() => {
            if (cancelled) return;
            widget = window.CentralChat.mount({
                container: hostRef.current,
                channelKey: CENTRAL_CHAT_KEY,
                mode: 'fill-container',
                locale: 'es',
                className: 'lito-chat__widget',
                onEvent: (event) => {
                    if (cancelled || !event || event.type !== 'conversationOpened') return;
                    openedAtRef.current = Date.now();
                    setConversationOpen(true);
                },
            });
            chatRef.current = widget;
            if (!widget.ready) throw new Error('central-chat no se pudo iniciar (revisar channel-key)');
            return widget.ready;
        });
        withTimeout(start, CENTRAL_CHAT_TIMEOUT_MS)
            .then(() => { if (!cancelled) setStatus('ready'); })
            .catch((err) => {
                console.warn('[centralChat] no disponible, se ofrece WhatsApp', err);
                if (!cancelled) setStatus('error');
            });
        return () => {
            cancelled = true;
            if (widget) widget.remove();
            chatRef.current = null;
            setStatus('loading');
            setConversationOpen(false);
        };
    }, [active, hostRef]);

    // A prefill that reaches central-chat while the conversation is still opening gets SENT as the
    // visitor's first message, and one shortly after gets cleared. There is no event for when it
    // settles, so wait PREFILL_DELAY_MS after conversationOpened: the text must only be proposed,
    // the visitor decides whether to send it.
    useEffect(() => {
        if (status !== 'ready' || !conversationOpen || !message || !chatRef.current) return;
        const chat = chatRef.current;
        const wait = Math.max(0, openedAtRef.current + PREFILL_DELAY_MS - Date.now());
        const timer = setTimeout(() => { chat.prefill(message).catch(() => {}); }, wait);
        return () => clearTimeout(timer);
    }, [status, conversationOpen, message]);

    return status;
}
